import { LalaSentry } from '@lendi/lala-utils';
import * as React from 'react';
import { Context } from './Context';
import { MockSession } from './MockSession';
import { Status } from './types';
var nonProductionDomain = [
    /^localhost$/,
    /^www\.lendi\.com\.au\.local$/,
    /^(www|team)\.lendi-(dev|stg)\.net$/,
    /^[^\/]+\.lendi-paas-(dev|stg)\.net$/,
];
export var mockAuthenticatedOutsideProduction = function (session, mockAuthenticated) {
    var isMockAuthenticated = typeof mockAuthenticated === 'function' ? mockAuthenticated() : mockAuthenticated;
    if (isMockAuthenticated &&
        typeof window !== 'undefined' &&
        nonProductionDomain.find(function (regex) { return window.location.hostname.match(regex); })) {
        var mockSession = new MockSession({
            brand: session.brand,
            type: session.type,
            environment: session.environment,
        });
        mockSession.setStatus(Status.Authenticated);
        return mockSession;
    }
    return session;
};
/**
 * This component is responsible for maintaining state about the auth session and passing it deep into the
 *  UI tree via the react context
 */
export var SessionProvider = function (_a) {
    var session = _a.session, developAsAuthenticated = _a.developAsAuthenticated, children = _a.children;
    var internalSession = React.useMemo(function () { return mockAuthenticatedOutsideProduction(session, developAsAuthenticated); }, []);
    var status = internalSession.status, error = internalSession.error, expiry = internalSession.expiry, token = internalSession.token, identity = internalSession.identity;
    // Using LalaSentry makes NextJs app consumers unhappy :(
    // tslint:disable-next-line no-console
    console.log('Initializing session context in provider', { status: status, error: error, expiry: expiry, identity: identity });
    var _b = React.useState({ status: status, error: error, expiry: expiry, token: token, identity: identity }), state = _b[0], setState = _b[1];
    var update = function () {
        var status = internalSession.status, error = internalSession.error, expiry = internalSession.expiry, token = internalSession.token, identity = internalSession.identity;
        LalaSentry.logInfo('Updating session context in provider', { status: status, error: error, expiry: expiry, identity: identity });
        setState({ status: status, error: error, expiry: expiry, token: token, identity: identity });
    };
    var savedSession = React.useRef(session);
    React.useEffect(function () {
        if (savedSession.current !== session) {
            LalaSentry.logError('Changing the session');
            throw new Error('Changing the session is not supported at this time.');
        }
    }, [session]);
    React.useEffect(function () {
        internalSession.on('session', update);
        internalSession.on('logout', update);
        internalSession.on('expire', update);
        internalSession.on('error', update);
        internalSession.listenToBroadcastChannel();
        return function () {
            internalSession.off('session', update);
            internalSession.off('logout', update);
            internalSession.off('expire', update);
            internalSession.off('error', update);
            internalSession.stopListeningToBroadcastChannel();
        };
    }, []);
    return (React.createElement(Context.Provider, { value: {
            status: state.status,
            error: state.error,
            expiry: state.expiry,
            token: state.token,
            identity: state.identity,
            brand: internalSession.brand,
            type: internalSession.type,
            environment: internalSession.environment,
            clearExpiry: function () { return internalSession.clearExpiry(); },
            refresh: function () { return internalSession.refresh(); },
            logout: function () { return internalSession.logout(); },
            DEPRECATED_RESET: function () { return internalSession.DEPRECATED_RESET(); },
            sendCode: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return internalSession.sendCode.apply(internalSession, args);
            },
            verifyCode: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return internalSession.verifyCode.apply(internalSession, args);
            },
            popupAuthorise: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return internalSession.popupAuthorise.apply(internalSession, args);
            },
            popupAuthoriseWithoutSetSession: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return internalSession.popupAuthoriseWithoutSetSession.apply(internalSession, args);
            },
            authorise: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return internalSession.authorise.apply(internalSession, args);
            },
        } }, children));
};
//# sourceMappingURL=SessionProvider.js.map